'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: 'What types of legal services do you offer?',
    answer: 'We specialize in international corporate law, including company formation, intellectual property protection, tax law, trade & customs compliance, regulatory compliance, and international litigation. Our services span across 12+ jurisdictions including Germany, UAE, USA, Poland, and more.'
  },
  {
    question: 'How quickly can you respond to my inquiry?',
    answer: 'We pride ourselves on rapid response times. You can expect an initial response within 24 hours of submitting your inquiry. For urgent matters, we recommend scheduling a direct consultation call through our Calendly link for immediate attention.'
  },
  {
    question: 'What is your consultation process?',
    answer: 'Our engagement process begins with an initial consultation where we assess your legal needs. Following this, we provide a detailed proposal outlining scope, timeline, and costs. Once agreed, we assign a dedicated team and maintain transparent communication throughout the engagement.'
  },
  {
    question: 'Do you work with international clients?',
    answer: 'Absolutely. International clients are our specialty. We have offices and partnerships across multiple continents and are experienced in navigating cross-border legal complexities. We work with clients from all over the world and can accommodate different time zones.'
  },
  {
    question: 'What are your fees and payment terms?',
    answer: 'Our fee structure varies depending on the complexity and scope of services required. We offer transparent pricing with options for fixed fees, hourly rates, or retainer agreements. Detailed fee information is provided during the initial consultation after we understand your specific needs.'
  },
  {
    question: 'How do you ensure confidentiality?',
    answer: 'Client confidentiality is paramount. All communications are protected by attorney-client privilege. We maintain strict data security protocols, use encrypted communication channels, and comply with GDPR and international data protection standards.'
  },
  {
    question: 'Can you help with company formation in multiple countries?',
    answer: 'Yes, we specialize in multi-jurisdictional company formation. Whether you need to establish entities in one country or coordinate formations across multiple jurisdictions, our global network and expertise ensure seamless execution with full regulatory compliance.'
  },
  {
    question: 'What languages do your attorneys speak?',
    answer: 'Our multilingual team speaks German, English, Polish, Thai, Arabic, Greek, and Mandarin Chinese. This linguistic diversity allows us to serve clients globally and navigate local legal systems effectively without language barriers.'
  }
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 px-6 md:px-12 max-w-6xl mx-auto">
      <div className="text-center mb-12">
        <span className="inline-block px-4 py-2 border border-border rounded-full text-xs uppercase tracking-widest text-muted-foreground mb-4">
          FAQ
        </span>
        <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground mb-4">
          Frequently Asked <span className="italic text-primary">Questions</span>
        </h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Find answers to common questions about our legal services and engagement process.
        </p>
      </div>

      <div className="space-y-4">
        {faqData.map((faq, index) => (
          <div
            key={index}
            className="glass-card rounded-xl border-2 border-primary/20 overflow-hidden transition-all hover:border-primary/40"
          >
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full px-6 py-5 flex items-center justify-between text-left transition-all hover:bg-primary/5"
            >
              <h3 className="text-lg font-semibold text-foreground pr-4">
                {faq.question}
              </h3>
              <Icon
                name="ChevronDownIcon"
                size={24}
                className={`text-primary flex-shrink-0 transition-transform duration-300 ${
                  openIndex === index ? 'rotate-180' : ''
                }`}
              />
            </button>
            
            <div
              className={`overflow-hidden transition-all duration-300 ${
                openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="px-6 pb-5 pt-2">
                <p className="text-muted-foreground leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center">
        <p className="text-muted-foreground mb-4">
          Still have questions? We're here to help.
        </p>
        <a
          href="#contact-form"
          className="inline-flex items-center gap-2 text-primary font-semibold hover:text-primary/80 transition-all"
        >
          <span>Contact us directly</span>
          <Icon name="ArrowRightIcon" size={18} />
        </a>
      </div>
    </section>
  );
}
